import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { SlidersHorizontal, Plus, Edit, Trash2, Loader2 } from 'lucide-react';

/**
 * Per-workload spot policy CRUD (Sprint 8 #5).
 *
 *  GET    /v1/clusters/{cid}/spot/policies            → list
 *  POST   /v1/clusters/{cid}/spot/policies            → create
 *  PUT    /v1/clusters/{cid}/spot/policies/{id}       → update
 *  DELETE /v1/clusters/{cid}/spot/policies/{id}       → delete
 *
 * Each policy declares a scope (namespace / namespace_selector / kind /
 * workload_name / labels) and the spot placement contract (replica_ratio,
 * min_on_demand_replicas, fallback_strategy, strategy, spot_ready_only,
 * mode).
 *
 * Backend resolves the most-specific matching policy per workload and the
 * rebalancer (Sprint 8 #6) emits drift-correction actions accordingly.
 */

const MODE_BADGE = {
  Auto: 'bg-emerald-100 text-emerald-800',
  Recommend: 'bg-amber-100 text-amber-800',
  Disabled: 'bg-gray-100 text-gray-600',
};

const STRATEGY_BADGE = {
  cost_optimized: 'bg-blue-50 text-blue-700',
  least_interrupted: 'bg-purple-50 text-purple-700',
};

const STRATEGY_LABEL = {
  cost_optimized: 'Cost-Optimized',
  least_interrupted: 'Least-Interrupted',
};

const empty = {
  name: '',
  namespace: '',
  namespace_selector: '',
  workload_kind: '',
  workload_name: '',
  label_selector_text: '',     // UI-only: parsed to JSON on submit
  replica_ratio: 0.7,
  min_on_demand_replicas: 1,
  fallback_strategy: 'immediate',
  strategy: 'cost_optimized',
  spot_ready_only: false,
  mode: 'Recommend',
  enabled: true,
  weight: 100,
};

const parseLabelSelector = (txt) => {
  if (!txt || !txt.trim()) return null;
  const out = {};
  txt.split(',').forEach((kv) => {
    const [k, v] = kv.split('=');
    if (k && v) out[k.trim()] = v.trim();
  });
  return Object.keys(out).length ? out : null;
};

const formatLabelSelector = (sel) => {
  if (!sel) return '';
  return Object.entries(sel)
    .map(([k, v]) => `${k}=${v}`)
    .join(',');
};

const PolicyModal = ({ open, onClose, clusterId, policy, onSaved }) => {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isEdit = !!policy?.policy_id;

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (policy) {
      setForm({
        name: policy.name || '',
        namespace: policy.namespace || '',
        namespace_selector: policy.namespace_selector || '',
        workload_kind: policy.workload_kind || '',
        workload_name: policy.workload_name || '',
        label_selector_text: formatLabelSelector(policy.label_selector),
        replica_ratio: policy.replica_ratio ?? 0.7,
        min_on_demand_replicas: policy.min_on_demand_replicas ?? 1,
        fallback_strategy: policy.fallback_strategy || 'immediate',
        strategy: policy.strategy || 'cost_optimized',
        spot_ready_only: !!policy.spot_ready_only,
        mode: policy.mode || 'Recommend',
        enabled: policy.enabled !== false,
        weight: policy.weight ?? 100,
      });
    } else {
      setForm(empty);
    }
  }, [open, policy]);

  const update = (k) => (e) => {
    const v = e?.target?.type === 'checkbox' ? e.target.checked : e?.target?.value ?? e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      name: form.name,
      namespace: form.namespace || null,
      namespace_selector: form.namespace_selector || null,
      workload_kind: form.workload_kind || null,
      workload_name: form.workload_name || null,
      label_selector: parseLabelSelector(form.label_selector_text),
      replica_ratio: Number(form.replica_ratio),
      min_on_demand_replicas: Number(form.min_on_demand_replicas),
      fallback_strategy: form.fallback_strategy,
      strategy: form.strategy,
      spot_ready_only: !!form.spot_ready_only,
      mode: form.mode,
      enabled: !!form.enabled,
      weight: Number(form.weight),
    };
    try {
      if (isEdit) {
        await axios.put(
          `${API}/v1/clusters/${clusterId}/spot/policies/${policy.policy_id}`,
          payload,
        );
      } else {
        await axios.post(
          `${API}/v1/clusters/${clusterId}/spot/policies`,
          payload,
        );
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit spot policy' : 'New spot policy'}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2">
            <Label className="text-xs">Display name</Label>
            <Input value={form.name} onChange={update('name')} placeholder="e.g. payments-staging spot" />
          </div>

          <div className="col-span-2 border-t pt-2">
            <div className="text-xs font-medium text-gray-700 mb-1">
              Scope (at least one required)
            </div>
          </div>
          <div>
            <Label className="text-xs">Namespace (exact)</Label>
            <Input value={form.namespace} onChange={update('namespace')} placeholder="prod" />
          </div>
          <div>
            <Label className="text-xs">Namespace selector (glob)</Label>
            <Input
              value={form.namespace_selector}
              onChange={update('namespace_selector')}
              placeholder="team-*"
            />
          </div>
          <div>
            <Label className="text-xs">Workload kind</Label>
            <Input
              value={form.workload_kind}
              onChange={update('workload_kind')}
              placeholder="Deployment"
            />
          </div>
          <div>
            <Label className="text-xs">Workload name (exact)</Label>
            <Input
              value={form.workload_name}
              onChange={update('workload_name')}
              placeholder="api"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Label selector (key=value, comma separated)</Label>
            <Input
              value={form.label_selector_text}
              onChange={update('label_selector_text')}
              placeholder="team=payments,tier=web"
            />
          </div>

          <div className="col-span-2 border-t pt-2">
            <div className="text-xs font-medium text-gray-700 mb-1">
              Spot placement
            </div>
          </div>
          <div>
            <Label className="text-xs">
              Replica ratio on spot ({(form.replica_ratio * 100).toFixed(0)}%)
            </Label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={form.replica_ratio}
              onChange={update('replica_ratio')}
              className="w-full"
            />
          </div>
          <div>
            <Label className="text-xs">Min on-demand replicas (safety floor)</Label>
            <Input
              type="number"
              min="0"
              value={form.min_on_demand_replicas}
              onChange={update('min_on_demand_replicas')}
            />
          </div>
          <div>
            <Label className="text-xs">Fallback strategy</Label>
            <select
              className="w-full text-sm border rounded px-2 py-1.5"
              value={form.fallback_strategy}
              onChange={update('fallback_strategy')}
            >
              <option value="immediate">immediate (drain on interrupt)</option>
              <option value="graceful">graceful (defer if PDB blocked)</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Mode</Label>
            <select
              className="w-full text-sm border rounded px-2 py-1.5"
              value={form.mode}
              onChange={update('mode')}
            >
              <option value="Recommend">Recommend (advisory only — never mutates)</option>
              <option value="Auto">Auto (enforced at admission by the pod-mutator)</option>
              <option value="Disabled">Disabled</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Distribution strategy</Label>
            <select
              className="w-full text-sm border rounded px-2 py-1.5"
              value={form.strategy}
              onChange={update('strategy')}
            >
              <option value="cost_optimized">Cost-Optimized (default)</option>
              <option value="least_interrupted">Least-Interrupted</option>
            </select>
            <p className="text-[10px] text-gray-500 mt-0.5">
              Cost-Optimized packs spot replicas onto the fewest cheapest
              nodes; Least-Interrupted spreads them across nodes so one spot
              reclaim hits fewer replicas.
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2 mt-5">
              <input
                type="checkbox"
                checked={form.spot_ready_only}
                onChange={update('spot_ready_only')}
                id="spot_ready_only"
              />
              <Label htmlFor="spot_ready_only" className="text-xs cursor-pointer">
                Spot-ready only
              </Label>
            </div>
            <p className="text-[10px] text-gray-500 mt-0.5">
              Only apply the split to workloads with more than 1 replica;
              single-replica services stay on-demand.
            </p>
          </div>
          <div>
            <Label className="text-xs">Weight (tiebreak when multiple policies match)</Label>
            <Input type="number" min="0" max="10000" value={form.weight} onChange={update('weight')} />
          </div>
          <div className="flex items-center gap-2 mt-5">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={update('enabled')}
              id="enabled"
            />
            <Label htmlFor="enabled" className="text-xs cursor-pointer">
              Enabled
            </Label>
          </div>
        </div>
        {error && <div className="text-xs text-red-600">{error}</div>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving || !form.name}>
            {saving ? (
              <>
                <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Saving…
              </>
            ) : isEdit ? (
              'Save changes'
            ) : (
              'Create policy'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const WorkloadSpotPoliciesPanel = ({ clusterId }) => {
  const [policies, setPolicies] = useState([]);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!clusterId) return;
    try {
      const res = await axios.get(`${API}/v1/clusters/${clusterId}/spot/policies`);
      setPolicies(res.data?.policies || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  }, [clusterId]);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 60000);
    return () => clearInterval(id);
  }, [fetchAll]);

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete policy "${p.name}"?`)) return;
    setDeletingId(p.policy_id);
    try {
      await axios.delete(
        `${API}/v1/clusters/${clusterId}/spot/policies/${p.policy_id}`,
      );
      await fetchAll();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const summarizeScope = (p) => {
    const parts = [];
    if (p.namespace) parts.push(`ns=${p.namespace}`);
    if (p.namespace_selector) parts.push(`ns~${p.namespace_selector}`);
    if (p.workload_kind) parts.push(`kind=${p.workload_kind}`);
    if (p.workload_name) parts.push(`name=${p.workload_name}`);
    if (p.label_selector && Object.keys(p.label_selector).length) {
      parts.push(`labels=${formatLabelSelector(p.label_selector)}`);
    }
    return parts.length ? parts.join(' • ') : 'cluster-wide';
  };

  if (!clusterId) return null;

  return (
    <Card className="mb-4">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-indigo-600" />
          Spot Policies (per workload)
          <Badge className="bg-indigo-50 text-indigo-700">
            {policies.length} policy{policies.length === 1 ? '' : 'ies'}
          </Badge>
        </CardTitle>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
        >
          <Plus className="h-3 w-3 mr-1" />
          New policy
        </Button>
      </CardHeader>
      <CardContent>
        <p className="text-[10px] text-gray-400 mb-2">
          Auto-mode policies are enforced in-cluster by the agent&apos;s
          pod-mutator admission webhook (capacity-type node affinity applied
          on pod create); Recommend-mode policies surface advisories only.
        </p>
        {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
        {policies.length === 0 ? (
          <p className="text-xs text-gray-500">
            No per-workload spot policies defined. Without policies, the
            cluster-level spot policy applies to every workload. Add a policy
            below to set a different replica ratio or on-demand floor for a
            specific namespace, kind, or label selector.
          </p>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left px-2 py-1.5">Name</th>
                  <th className="text-left px-2 py-1.5">Scope</th>
                  <th className="text-right px-2 py-1.5">Spot %</th>
                  <th className="text-right px-2 py-1.5">Min OD</th>
                  <th className="text-center px-2 py-1.5">Strategy</th>
                  <th className="text-center px-2 py-1.5">Mode</th>
                  <th className="text-right px-2 py-1.5">Weight</th>
                  <th className="px-2 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {policies.map((p) => (
                  <tr key={p.policy_id} className="border-t hover:bg-gray-50">
                    <td className="px-2 py-1.5 font-medium">
                      {p.name}
                      {!p.enabled && (
                        <Badge className="ml-1 bg-gray-200 text-gray-700 text-[10px]">
                          disabled
                        </Badge>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-gray-600">
                      <code className="text-[10px]">{summarizeScope(p)}</code>
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      {(p.replica_ratio * 100).toFixed(0)}%
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      {p.min_on_demand_replicas}
                    </td>
                    <td className="px-2 py-1.5 text-center whitespace-nowrap">
                      <Badge
                        className={`${STRATEGY_BADGE[p.strategy] || STRATEGY_BADGE.cost_optimized} text-[10px]`}
                      >
                        {STRATEGY_LABEL[p.strategy] || STRATEGY_LABEL.cost_optimized}
                      </Badge>
                      {p.spot_ready_only && (
                        <Badge className="ml-1 bg-teal-50 text-teal-700 text-[10px]">
                          spot-ready
                        </Badge>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <Badge className={MODE_BADGE[p.mode] || 'bg-gray-100 text-gray-700'}>
                        {p.mode}
                      </Badge>
                    </td>
                    <td className="px-2 py-1.5 text-right">{p.weight}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditing(p);
                          setModalOpen(true);
                        }}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(p)}
                        disabled={deletingId === p.policy_id}
                      >
                        {deletingId === p.policy_id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Trash2 className="h-3 w-3 text-red-500" />
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      <PolicyModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        clusterId={clusterId}
        policy={editing}
        onSaved={fetchAll}
      />
    </Card>
  );
};

export default WorkloadSpotPoliciesPanel;
